import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useAuthority } from '@/hooks/useAuthority'
import { CredentialingTypesService, type CredentialingProviderType } from '@/services/modules/CredentialingService'
import { toast } from '@/components/ui/Toast'

export function CredentialingTypesPage() {
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar tipo' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(CredentialingTypesService, { size: 15, reloadKey: reload , filters: sf.filters})

  const exp = useDataTableExport({
    filename: 'types',
    service: CredentialingTypesService as never,
    filters: sf.filters,
    columns: [], // TODO: configurar columns para export
  })
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: CredentialingProviderType }>({ open: false })
  const [openDelete, setOpenDelete] = useState<CredentialingProviderType | null>(null)
  const { can } = useAuthority()
  const canCreate = can('credentialing.types.create')
  const canEdit = can('credentialing.types.edit')
  const canDelete = can('credentialing.types.delete')

  async function save(payload: Record<string, unknown>) {
    try {
      if (openForm.item) { await CredentialingTypesService.update(openForm.item.id, payload); toast.success('Tipo atualizado.') }
      else { await CredentialingTypesService.create(payload); toast.success('Tipo criado.') }
      setOpenForm({ open: false }); setReload(v => v + 1)
    } catch (e) { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro.') }
  }
  async function del() {
    if (!openDelete) return
    try { await CredentialingTypesService.destroy(openDelete.id); toast.success('Tipo removido.'); setOpenDelete(null); setReload(v => v + 1) }
    catch { toast.error('Erro.') }
  }

  const columns: DataTableColumn<CredentialingProviderType>[] = [
    {
      key: 'type', header: 'Tipo', sortable: true, alwaysVisible: true,
      // Mostra o caminho completo da árvore (ex.: "Exame > Exame de sangue").
      // O último nó (o próprio tipo) fica destacado, os ancestrais em cinza.
      render: t => <TypeBreadcrumb path={t.path} />,
    },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: t => (
        <StatusDot value={t.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader title="Tipos de credenciado" subtitle="Hierarquia (médico, especialidades, clínicas, )"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Credenciamento' }, { label: 'Tipos' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Novo tipo</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="credentialing.types"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={(t) => [
          ...(canEdit ? [{ key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />,  onClick: () => setOpenForm({ open: true, item: t }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />,  danger: true, divider: canEdit, onClick: () => setOpenDelete(t) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <TypeForm open={openForm.open} item={openForm.item} all={rows} onClose={() => setOpenForm({ open: false })} onSave={save} />
      <ConfirmDialog open={!!openDelete} onClose={() => setOpenDelete(null)} onConfirm={del}
        title="Remover tipo?" description={`Tem certeza que deseja remover "${openDelete?.type}"?`} confirmLabel="Remover" variant="danger" />  
    {sf.drawer}
    </>
  )
}

/**
 * Renderiza o caminho hierárquico ("Exame > Exame de sangue > Hemograma")
 * com os ancestrais em cinza claro e o nó folha em destaque.
 */
function TypeBreadcrumb({ path }: { path: string }) {
  if (!path) return <span className="text-gray-400">—</span>
  const parts = path.split(' > ')
  const leaf = parts.pop() ?? ''
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {parts.map((p, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          <span className="text-[12px] text-gray-400">{p}</span>
          <span className="text-gray-300 text-[11px]">›</span>
        </span>
      ))}
      <span className="font-medium text-gray-800">{leaf}</span>
    </div>
  )
}

function TypeForm({ open, item, all, onClose, onSave }: { open: boolean; item?: CredentialingProviderType; all: CredentialingProviderType[]; onClose: () => void; onSave: (payload: Record<string, unknown>) => Promise<void> }) {
  const [typeName, setTypeName] = useState('')
  const [parentId, setParentId] = useState('')
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (open) { setTypeName(item?.type ?? ''); setParentId(item?.parentId ?? ''); setStatus(item?.status ?? 'ATIVO') }
  }, [open, item])
  async function submit() { setSaving(true); await onSave({ type: typeName, parent_id: parentId || null, status }); setSaving(false) }
  return (
    <Modal open={open} onClose={onClose} title={item ? 'Editar tipo' : 'Novo tipo'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={submit} loading={saving} disabled={!typeName.trim()}>Salvar</Button></>}>
      <div className="space-y-3">
        <Input label="Nome" required value={typeName} onChange={e => setTypeName(e.target.value)} placeholder="Cardiologista" />
        <Select
          label="Pertence ao tipo"
          value={parentId}
          onChange={e => setParentId(e.target.value)}
          placeholder="— Sem categoria pai (será uma raiz) —"
          // Mostra o caminho completo no rótulo: "Exame > Exame de sangue".
          // Isso evita ambiguidade quando há tipos com o mesmo nome em árvores
          // diferentes (ex.: "Endoscopia" filho de "Exame" vs filho de "Cirurgia").
          options={all
            .filter(t => t.id !== item?.id)
            .map(t => ({ value: t.id, label: t.path || t.type }))}
          hint="Categoriza este tipo dentro de outro. Deixe vazio para criar uma raiz da árvore."
        />
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}
